import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import { FiPlus, FiEdit2, FiTrash2, FiStar, FiCamera } from "react-icons/fi";
import api, { imgUrl } from "../utils/api";
import Loader from "../components/Loader";
import EmptyState from "../components/EmptyState";

export default function GalleryList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchItems = () => {
    setLoading(true);
    api
      .get("/gallery", { params: { page: 1, limit: 200 } })
      .then((res) => setItems(res.data.items))
      .catch(() => toast.error("Failed to load gallery items"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/gallery/${id}`);
      toast.success("Gallery item deleted");
      setItems(items.filter((i) => i._id !== id));
    } catch {
      toast.error("Failed to delete");
    }
  };

  const toggleFeatured = async (item) => {
    try {
      const { data } = await api.put(`/gallery/${item._id}`, { featured: !item.featured });
      setItems(items.map((i) => (i._id === item._id ? data : i)));
      toast.success(data.featured ? "Marked as featured" : "Removed from featured");
    } catch {
      toast.error("Failed to update");
    }
  };

  if (loading) return <Loader />;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-2xl text-maroon-800">Photo Gallery</h1>
        <Link
          to="/admin/gallery/new"
          className="inline-flex items-center gap-2 bg-saffron-500 hover:bg-saffron-600 text-white px-5 py-2.5 rounded-full text-sm font-semibold transition-colors"
        >
          <FiPlus /> Add Gallery Item
        </Link>
      </div>

      {items.length === 0 ? (
        <EmptyState
          title="No gallery items yet"
          subtitle="Click 'Add Gallery Item' to upload photos of poojas, ceremonies and events."
        />
      ) : (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-left">
              <tr>
                <th className="px-5 py-3 font-medium">Preview</th>
                <th className="px-5 py-3 font-medium">Title</th>
                <th className="px-5 py-3 font-medium">Category</th>
                <th className="px-5 py-3 font-medium">Photos</th>
                <th className="px-5 py-3 font-medium">Featured</th>
                <th className="px-5 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item._id} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="px-5 py-3">
                    <div className="w-12 h-12 rounded-lg bg-gold-50 overflow-hidden flex items-center justify-center">
                      {item.images && item.images[0] ? (
                        <img src={imgUrl(item.images[0])} alt={item.title} className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-xl"><FiCamera size={20} /></span>
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-3 font-medium text-maroon-800 max-w-xs truncate">{item.title}</td>
                  <td className="px-5 py-3 text-gray-600">{item.category}</td>
                  <td className="px-5 py-3 text-gray-600">{item.images?.length || 0}</td>
                  <td className="px-5 py-3">
                    <button
                      onClick={() => toggleFeatured(item)}
                      className={`p-1.5 rounded-lg transition-colors ${
                        item.featured ? "text-gold-500" : "text-gray-300 hover:text-gold-400"
                      }`}
                      title={item.featured ? "Remove from featured" : "Mark as featured"}
                    >
                      <FiStar fill={item.featured ? "currentColor" : "none"} />
                    </button>
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <Link to={`/admin/gallery/edit/${item._id}`} className="p-2 rounded-lg hover:bg-gray-100 text-saffron-600">
                        <FiEdit2 />
                      </Link>
                      <button
                        onClick={() => handleDelete(item._id, item.title)}
                        className="p-2 rounded-lg hover:bg-gray-100 text-red-500"
                      >
                        <FiTrash2 />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
